import React from 'react';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import type { ApolloError } from 'apollo-client';

import { CommonActions, HomeActions, UserActions } from '@atlassian/navigation-system/top-nav';

import {
	Attribution,
	ErrorDisplay,
	withErrorBoundary,
	isUnauthorizedError,
	isNotFoundError,
} from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import type { SessionDataType } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';

import { AppSwitcherButton } from '../AppSwitcherButton';
import { LoginButton } from '../LoginButton';
import { NotificationsButton } from '../NotificationsButton';
import { RovoButton } from '../RovoButton';
import { SearchBar } from '../SearchBar';
import { AppNavigationUnifiedQuery } from '../Nav3/AppNavigationUnifiedQuery.graphql';
import type {
	AppNavigationUnifiedQuery as AppNavigationUnifiedQueryType,
	AppNavigationUnifiedQuery_user_AtlassianUser as AppNavigationUnifiedQuery_user,
} from '../Nav3/__types__/AppNavigationUnifiedQuery';

import { CompanyHubProfileMenu } from './CompanyHubProfileMenu';
import type {
	CompanyHubTopNavigationQuery,
	CompanyHubTopNavigationQuery_user,
} from './__types__/CompanyHubTopNavigationQuery';
import { CompanyHubSiteLogoButton } from './CompanyHubSiteLogoButton';

export type User = {
	displayName: SessionDataType['displayName'];
	photos: AppNavigationUnifiedQuery_user['photos'];
	emails: CompanyHubTopNavigationQuery_user['emails'];
	environment: SessionDataType['environment'];
};

/**
 * The top navigation of the Company Hub app-like experience at Team '25 US.
 *
 * Inspired by `TopNavigation` at confluence/next/packages/app-navigation/src/TopNavigation.tsx
 *
 * @see {@link https://www.figma.com/design/MBdUXhdmgbsIkMkOhQGu5e/Hub-App?node-id=101-52260}
 * @see {@link https://hello.atlassian.net/wiki/spaces/CLI/pages/4856024337/Confluence-less+Team+25+US+Experience}
 */
function CompanyHubTopNavigationComponent() {
	const { displayName, isLoggedIn, environment } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { data: emailData, error: emailError } = useQuery<CompanyHubTopNavigationQuery>(
		gql`
			query CompanyHubTopNavigationQuery {
				user(current: true) {
					id
					emails {
						value
						isPrimary
					}
				}
			}
		`,
		{
			// Skip if we are in SSR mode, we only want to call this query in SPA
			skip: renderServerPlaceholder,
		},
	);
	// ATTENTION: Reusing an existing query here is not best practice (do not replicate)! This decision was justified
	// because Company Hub will eventually leave Confluence and we want to reduce any potential issues with SSR until then.
	const { data: photoData, error: photoError } = useQuery<AppNavigationUnifiedQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		AppNavigationUnifiedQuery,
	);

	const user: User = {
		displayName,
		photos: photoData?.user?.photos ?? null,
		emails: emailData?.user?.emails ?? null,
		environment,
	};

	const renderError = (error: ApolloError) => {
		if (isUnauthorizedError(error) || isNotFoundError(error)) {
			markErrorAsHandled(error);
			return null;
		}
		return <ErrorDisplay error={error} />;
	};

	return (
		<>
			{/* There's no SideNavToggleButton here because:
			 * - there's no side nav in view or edit mode;
			 * - there's side nav in settings screens, but there'd be shift to the right if SideNavToggleButton were rendered.
			 */}
			<HomeActions>
				<AppSwitcherButton />
				{/* DeveloperButtons here would be nice, but isn't required. */}
				<CompanyHubSiteLogoButton />
			</HomeActions>
			<CommonActions>
				<SearchBar />
			</CommonActions>
			<UserActions>
				<RovoButton />
				<NotificationsButton user={user} />
				{isLoggedIn ? <CompanyHubProfileMenu user={user} /> : <LoginButton />}
			</UserActions>
			{emailError && renderError(emailError)}
			{photoError && renderError(photoError)}
		</>
	);
}

export const CompanyHubTopNavigation = withErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
	attributes: {
		// Inspired by `TopNavigationBase` at confluence/next/packages/app-navigation/entry-points/topNavigation.tsx
		errorBoundaryId: 'company-hub/top-navigation',
	},
})(CompanyHubTopNavigationComponent);
